import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Swal from "sweetalert2";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  makeStyles
} from "@material-ui/core";
import { Check } from "@material-ui/icons";
import Datetime from "react-datetime";
import styles from "assets/jss/material-dashboard-pro-react/views/formsStyle.js";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import CustomInput from "components/CustomInput/CustomInput";
import Button from "components/CustomButtons/Button.js";
import BodyHtmlInput from "views/OnlineBookingHomeContent/BodyHtmlInput";
import { HtmlPreview } from "./AnnouncementShared";
import AnnouncementRichText, { findUnsupported } from "./AnnouncementRichText";

const useStyles = makeStyles(styles);

const AnnouncementForm = ({ input, setInput, disabled, onSubmit, ready }) => {
  const classes = useStyles();
  const [mode, setMode] = useState(null);
  const [visualKey, setVisualKey] = useState(0);

  // Open in the visual editor unless that would drop parts of the content.
  useEffect(() => {
    if (ready && mode === null)
      setMode(findUnsupported(input.contentHtml).length ? "html" : "visual");
  }, [ready]);

  const openVisual = () => {
    setVisualKey(key => key + 1);
    setMode("visual");
  };

  const switchToVisual = () => {
    if (mode === "visual") return;
    const issues = findUnsupported(input.contentHtml);
    if (!issues.length) {
      openVisual();
      return;
    }
    Swal.fire({
      icon: "warning",
      title: "Some content can't be edited visually",
      html:
        "The visual editor doesn't support: <b>" +
        issues.join(", ") +
        "</b>.<br><br>The content is only changed once you type in the editor, " +
        "but any edit there will remove these parts.",
      showCancelButton: true,
      confirmButtonText: "Open Visual Editor anyway",
      cancelButtonText: "Stay in HTML"
    }).then(result => {
      if (result.isConfirmed) openVisual();
    });
  };

  const handleContentChanged = value =>
    setInput(prev => ({ ...prev, contentHtml: value }));

  // Read the value before setInput: React 16 clears the event once the handler returns.
  const handleTextChanged = field => e => {
    const { value } = e.target;
    setInput(prev => ({ ...prev, [field]: value }));
  };

  const handleDateChanged = field => e =>
    setInput(prev => ({ ...prev, [field]: e === "" ? null : e }));

  return (
    <form onSubmit={onSubmit}>
      <CustomInput
        labelText="Title *"
        formControlProps={{ fullWidth: true }}
        inputProps={{
          onChange: handleTextChanged("title"),
          value: input.title
        }}
      />
      <CustomInput
        labelText="Slug (leave empty to generate from Title)"
        helperText={
          "Lowercase letters, numbers and hyphens only. Page URL: /announcements/" +
          (input.slug || "{generated-from-title}")
        }
        formControlProps={{ fullWidth: true }}
        inputProps={{
          onChange: handleTextChanged("slug"),
          value: input.slug
        }}
      />
      <CustomInput
        labelText="Subtitle (also used as the page meta description)"
        formControlProps={{ fullWidth: true }}
        inputProps={{
          onChange: handleTextChanged("subtitle"),
          value: input.subtitle
        }}
      />
      <CustomInput
        labelText="Summary * (shown on the announcement cards)"
        formControlProps={{ fullWidth: true }}
        inputProps={{
          multiline: true,
          rows: 3,
          onChange: handleTextChanged("summary"),
          value: input.summary
        }}
      />
      <p className={classes.label}>
        The banner is managed separately: use Upload Banner in the announcement
        list after saving.
      </p>

      <GridContainer>
        <GridItem xs={12} sm={6}>
          <FormControl fullWidth>
            <Datetime
              value={input.publishDate}
              onChange={handleDateChanged("publishDate")}
              timeFormat={true}
              closeOnSelect={true}
              inputProps={{ placeholder: "Publish Date *" }}
            />
          </FormControl>
        </GridItem>
        <GridItem xs={12} sm={6}>
          <FormControl fullWidth>
            <Datetime
              value={input.expiryDate}
              onChange={handleDateChanged("expiryDate")}
              timeFormat={true}
              closeOnSelect={true}
              inputProps={{
                placeholder: "Expiry Date (leave empty for no expiry)"
              }}
            />
          </FormControl>
        </GridItem>
      </GridContainer>

      <FormControlLabel
        control={
          <Checkbox
            checked={input.isActive}
            onChange={e => {
              const { checked } = e.target;
              setInput(prev => ({ ...prev, isActive: checked }));
            }}
            checkedIcon={<Check className={classes.checkedIcon} />}
            icon={<Check className={classes.uncheckedIcon} />}
            classes={{ checked: classes.checked, root: classes.checkRoot }}
          />
        }
        classes={{ label: classes.label, root: classes.labelRoot }}
        label="Is Active"
      />

      <p className={classes.label} style={{ marginTop: 24 }}>
        Content
      </p>
      <div style={{ marginBottom: 8 }}>
        <Button
          size="sm"
          color={mode === "visual" ? "info" : "white"}
          onClick={switchToVisual}
          disabled={mode === null}
        >
          Visual Editor
        </Button>
        <Button
          size="sm"
          color={mode === "html" ? "info" : "white"}
          onClick={() => setMode("html")}
          disabled={mode === null}
        >
          HTML / Upload
        </Button>
      </div>

      {mode === "visual" && (
        <AnnouncementRichText
          key={visualKey}
          html={input.contentHtml}
          onChange={handleContentChanged}
        />
      )}
      {mode === "html" && (
        <BodyHtmlInput
          labelText="Content HTML"
          value={input.contentHtml}
          onChange={handleContentChanged}
        />
      )}
      <p className={classes.label}>
        {mode === "visual"
          ? "Start from the greeting and end at the closing line. The signature and contact card are added by the website."
          : "Start from the greeting and end at the closing line. The signature and contact card are added by the website. Available classes: greeting, main-text, italic, table-wrapper, value."}
      </p>
      <p className={classes.label}>Preview</p>
      <HtmlPreview html={input.contentHtml} />

      <Button disabled={disabled} color="rose" type="submit" value="Submit">
        Submit
      </Button>
    </form>
  );
};

export default AnnouncementForm;

AnnouncementForm.propTypes = {
  input: PropTypes.object,
  setInput: PropTypes.func,
  disabled: PropTypes.bool,
  onSubmit: PropTypes.func,
  ready: PropTypes.bool
};
